import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const NEWSLETTER_URL = 'https://readdy.ai/api/form/d75bs0r41deorl02f97g';
const WA_URL = 'https://wa.me/573228000501';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

export default function SiteFooter() {
  const [status, setStatus] = useState<FormStatus>('idle');

  const handleNewsletter = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((v, k) => body.append(k, v.toString()));

    setStatus('sending');
    try {
      const res = await fetch(NEWSLETTER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) form.reset();
    } catch {
      setStatus('error');
    }
  };

  return (
    <footer className="bg-charcoal text-white rounded-t-[32px] mt-20">
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-8">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Newsletter */}
          <div className="md:col-span-1">
            <h3 className="font-serif text-3xl font-bold mb-4">
              Únete a Nuestra Comunidad
            </h3>
            <p className="font-sans text-sm text-gray-light mb-6">
              Recibe ofertas exclusivas y novedades
            </p>
            {status === 'success' ? (
              <p className="font-sans text-sm text-[#25D366] py-3">
                ✓ ¡Gracias! Ya estás suscrita.
              </p>
            ) : (
              <form
                onSubmit={handleNewsletter}
                data-readdy-form={true}
                id="newsletter-subscription"
                className="space-y-4"
              >
                <input
                  name="email"
                  type="email"
                  placeholder="Tu correo electrónico"
                  required
                  className="w-full bg-transparent border-b-2 border-gold py-3 px-0 text-white placeholder-gray-light focus:outline-none focus:border-gold/80 transition-colors text-sm"
                />
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="bg-white text-charcoal px-8 py-3 rounded-full font-sans font-medium hover:bg-sand transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {status === 'sending' ? 'Enviando...' : 'Suscribirme'}
                  <i className="ri-arrow-right-line text-gold" />
                </button>
              </form>
            )}
          </div>

          {/* Nav links */}
          <div>
            <h4 className="font-sans text-xs text-gold uppercase tracking-wide mb-6">
              Explorar
            </h4>
            <nav className="space-y-4">
              {[
                { label: 'Perfumes', id: 'perfumes' },
                { label: 'Moda Femenina', id: 'ropa' },
                { label: 'Sobre Nosotros', id: 'sobre-nosotros' },
                { label: 'Contacto', id: 'contacto' },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="block font-sans text-sm text-white hover:text-gold transition-colors cursor-pointer text-left"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-sans text-xs text-gold uppercase tracking-wide mb-6">
              Contacto
            </h4>
            <div className="space-y-4 font-sans text-sm">
              <a
                href="tel:+573228000501"
                className="block text-white hover:text-gold transition-colors cursor-pointer"
              >
                +57 322 8000 501
              </a>
              <div className="pt-4">
                <h5 className="text-xs text-gold uppercase tracking-wide mb-3">
                  Ubicación
                </h5>
                <p className="text-gray-light leading-relaxed">
                  Carrera 19 Nº4D-37
                  <br />
                  Barrio Villa Alejandra
                  <br />
                  Villavicencio, Meta, Colombia
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-gold/20 mb-8" />

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <a
              href="https://www.instagram.com/laurasoto_shop"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 border border-white/30 rounded-full flex items-center justify-center hover:border-gold hover:bg-gold/10 transition-all cursor-pointer"
              aria-label="Instagram"
            >
              <i className="ri-instagram-line text-lg" />
            </a>
            <a
              href="https://www.tiktok.com/@laurasoto_shop"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 border border-white/30 rounded-full flex items-center justify-center hover:border-gold hover:bg-gold/10 transition-all cursor-pointer"
              aria-label="TikTok"
            >
              <i className="ri-tiktok-line text-lg" />
            </a>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 border border-white/30 rounded-full flex items-center justify-center hover:border-gold hover:bg-gold/10 transition-all cursor-pointer"
              aria-label="WhatsApp"
            >
              <i className="ri-whatsapp-line text-lg" />
            </a>
          </div>
          <p className="font-sans text-xs text-gray-light text-center">
            © 2024 Laura Soto Shop. Todos los derechos reservados
          </p>
          <div className="flex items-center gap-4 font-sans text-xs">
            <Link to="/terminos" className="text-white hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
              Términos de Servicio
            </Link>
            <span className="text-gold">|</span>
            <Link to="/privacidad" className="text-white hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
              Política de Privacidad
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
